import React, { FunctionComponent, SyntheticEvent, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import envelopeIcon from '../../assets/svg/envelope-open-message-primary.svg';
import userIcon from '../../assets/svg/user-icon.svg';
import checkCircleIcon from '../../assets/images/project/check-circle.svg';
import binocularsIcon from '../../assets/svg/binoculars-dark-grey.svg';
// import plusIcon from '../../assets/svg/plus-transparent.svg';
import { MessageContext } from '../../contexts/messageContext';
import { GET_CURRENT_USER_me } from '../../graphql/types/GET_CURRENT_USER';
import { getCompressedImagePath } from '../../utils/getCompressedImagePath';
import CollaborationStatus, { ECollaborationStatus } from './CollaborationStatus';
import { UserData, getUserAvatar, getUserName, getUserBio, getUserProfessions, getUserCollaborationStatus, getUserDesiredCollaboration } from '../../types/user';

interface IUserCreativeCardModernProps {
    user: UserData;
    currentUser: GET_CURRENT_USER_me;
}

export const DEFAULT_MEDIA_AVATAR = 'https://www.gravatar.com/avatar?d=mp';
const MAX_BIO_LENGTH = 120;

const UserCreativeCardModern: FunctionComponent<IUserCreativeCardModernProps> = ({ user, currentUser }) => {
    const { actions } = useContext(MessageContext);
    const [isSelecting, setIsSelecting] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    // const [isInviting, setIsInviting] = useState(false);

    const handleSendMessageOnClick = async (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsSelecting(true);
        try {
            await actions.handleSelectOtherUser(user);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSelecting(false);
        }
    };
    // const handleInviteCoffeeChat = async (e: SyntheticEvent) => {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     setIsInviting(true);
    //     try {
    //         await actions.handleCreateCoffeeChatRoom([Number(user.id)]);
    //     } catch (error) {
    //         console.error(error);
    //     } finally {
    //         setIsInviting(false);
    //     }
    // };

    const userAvatar = getUserAvatar(user);
    const compressedAvatarImage = getCompressedImagePath(userAvatar, 'thumbnail');
    const isDefaultMediaAvatar = userAvatar === DEFAULT_MEDIA_AVATAR;
    const userName = getUserName(user);
    const userBio = getUserBio(user);
    const professions = getUserProfessions(user);
    const collaborationStatus = getUserCollaborationStatus(user);
    const desiredCollaboration = getUserDesiredCollaboration(user);

    const truncatedBio = userBio && userBio.length > MAX_BIO_LENGTH
        ? `${userBio.substring(0, MAX_BIO_LENGTH)}...`
        : userBio;

    return (
        <div className="column is-one-third-desktop is-half-tablet is-full-mobile">
            <div
                className={`modern-user-card ${isHovered ? 'is-hovered' : ''}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {collaborationStatus && (
                    <div className="modern-user-card__status-badge">
                        <CollaborationStatus
                            collaborationStatus={collaborationStatus as ECollaborationStatus}
                        />
                        <div className="modern-user-card__status-dots">
                            <span className="modern-user-card__status-dot"></span>
                            <span className="modern-user-card__status-dot"></span>
                            <span className="modern-user-card__status-dot"></span>
                        </div>
                    </div>
                )}

                <Link to={`/user/${user.id}`} className="modern-user-card__link">
                    <div className="modern-user-card__header-row">
                        <div className="modern-user-card__avatar-container">
                            <div
                                className={`modern-user-card__avatar ${isDefaultMediaAvatar ? 'is-default' : ''}`}
                                style={{
                                    backgroundImage: `url(${compressedAvatarImage || userIcon})`
                                }}
                            />
                        </div>
                    </div>

                    <div className="modern-user-card__content">
                        <h3 className="modern-user-card__name">{userName}</h3>

                        {professions && professions.length > 0 && (
                            <div className="modern-user-card__professions-container">
                                {professions.map((prof, idx) => (
                                    <span key={idx} className="modern-user-card__profession-tag">
                                        <img
                                            src={checkCircleIcon}
                                            alt="check"
                                            className="modern-user-card__profession-icon"
                                        />
                                        {prof.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {truncatedBio && (
                            <p className="modern-user-card__bio">{truncatedBio}</p>
                        )}

                        {desiredCollaboration && (
                            <div className="modern-user-card__collaborate">
                                <p className="modern-user-card__collaborate-label">
                                    <img
                                        src={binocularsIcon}
                                        alt="binoculars"
                                        className="modern-user-card__collaborate-icon"
                                    />
                                    Want to collaborate with
                                </p>
                                <p className="modern-user-card__collaborate-value">
                                    {desiredCollaboration.label}
                                </p>
                            </div>
                        )}
                    </div>
                </Link>

                {user.id.toString() !== currentUser.id && (
                    <div className="modern-user-card__footer">
                        <button
                            className={`modern-user-card__message-btn ${isSelecting ? 'is-loading' : ''}`}
                            onClick={handleSendMessageOnClick}
                            disabled={isSelecting}
                        >
                            <img src={envelopeIcon} alt="Send message" className="modern-user-card__message-icon" />
                            <span>{isSelecting ? 'Sending...' : 'Message'}</span>
                        </button>

                        {/*
                            IMPORTANT: DO NOT DELETE THIS CODE
                            Invite button is temporarily disabled.
                            It will be re-enabled in a future update.
                            Please keep this code commented for now.
                        */}
                        {/* <button
                            className={`modern-user-card__invite-btn ${isInviting ? 'is-loading' : ''}`}
                            onClick={handleInviteCoffeeChat}
                            disabled={isInviting}
                        >
                            <img
                                src={plusIcon}
                                alt="Plus"
                                className="modern-user-card__invite-icon-img"
                            />
                            <span>{isInviting ? 'Inviting...' : 'Invite'}</span>
                        </button> */}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserCreativeCardModern;
